import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  User,
  GraduationCap,
  Briefcase,
  MapPin,
  FileText,
  LinkedinLogo,
  Upload,
  CheckCircle,
  ArrowLeft,
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import InputField from '../components/talent/InputField';
import { MAX_CV_BYTES, MAX_CV_MB } from '../constants/talentBank';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${API_URL}/api`;

const INITIAL = {
  full_name: '',
  city: '',
  nationality: '',
  email: '',
  mobile: '',
  academic_qualification: '',
  specialization: '',
  years_of_experience: '',
  current_or_previous_role: '',
  professional_skills: '',
  courses_and_certifications: '',
  preferred_areas_of_interest: '',
  preferred_city_or_work_location: '',
  preferred_work_mode: '',
  has_consultant_license: '',
  licensing_authority: '',
  short_professional_bio: '',
  linkedin_profile_url: '',
  consent_accepted: false,
};

const WORK_MODES = [
  { value: 'remote', label: 'عن بُعد (أونلاين)' },
  { value: 'onsite', label: 'ميداني (حضوري)' },
  { value: 'hybrid', label: 'هجين (جزئي)' },
  { value: 'flexible', label: 'مرن (حسب الاتفاق)' },
];

const CreateProfilePage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState(INITIAL);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [cvFile, setCvFile] = useState(null);
  const [cvUploading, setCvUploading] = useState(false);
  const [cvUrl, setCvUrl] = useState('');
  const [success, setSuccess] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((p) => ({ ...p, [name]: type === 'checkbox' ? checked : value }));
    if (errors[name]) setErrors((p) => ({ ...p, [name]: '' }));
  };

  const handleCvUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('يُسمح فقط بملفات PDF');
      return;
    }
    if (file.size > MAX_CV_BYTES) {
      toast.error(`الحجم الأقصى ${MAX_CV_MB} ميجابايت`);
      return;
    }
    setCvFile(file);
    setCvUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`${API}/upload-cv`, { method: 'POST', body: fd });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'فشل رفع الملف');
      setCvUrl(data.cv_file_url);
      toast.success('تم رفع السيرة الذاتية');
    } catch (err) {
      toast.error(err.message);
      setCvFile(null);
    } finally {
      setCvUploading(false);
    }
  };

  const validate = () => {
    const e = {};
    const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.full_name.trim()) e.full_name = 'الاسم الكامل مطلوب';
    if (!formData.city.trim()) e.city = 'المدينة مطلوبة';
    if (!formData.email.trim()) e.email = 'البريد الإلكتروني مطلوب';
    else if (!emailRe.test(formData.email)) e.email = 'البريد الإلكتروني غير صالح';
    if (!formData.mobile.trim()) e.mobile = 'رقم الجوال مطلوب';
    if (!formData.academic_qualification.trim())
      e.academic_qualification = 'المؤهل العلمي مطلوب';
    if (!formData.specialization.trim()) e.specialization = 'التخصص مطلوب';
    if (!formData.years_of_experience.trim())
      e.years_of_experience = 'سنوات الخبرة مطلوبة';
    if (!formData.consent_accepted) e.consent_accepted = 'يجب الموافقة على الإقرار';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      toast.error('يرجى إكمال الحقول المطلوبة');
      return;
    }
    setLoading(true);
    try {
      // Normalise the license fields before sending: convert the string
      // 'yes'/'no' radio value to a real boolean (or null if unanswered)
      // and blank out the authority when license == no.
      const payload = { ...formData };
      if (payload.has_consultant_license === 'yes') {
        payload.has_consultant_license = true;
      } else if (payload.has_consultant_license === 'no') {
        payload.has_consultant_license = false;
        payload.licensing_authority = '';
      } else {
        payload.has_consultant_license = null;
        payload.licensing_authority = '';
      }
      const res = await fetch(`${API}/professional-profiles`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'فشل تقديم الملف');
      if (cvUrl) {
        await fetch(
          `${API}/professional-profiles/${data.id}/cv?cv_file_url=${encodeURIComponent(cvUrl)}`,
          { method: 'PATCH' }
        );
      }
      setSuccess(true);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const field = (props) => (
    <InputField
      {...props}
      value={formData[props.name]}
      onChange={handleChange}
      error={errors[props.name]}
    />
  );

  if (success) {
    return (
      <main
        data-testid="create-profile-success"
        className="pt-24 pb-20 min-h-screen bg-[#FAFAF7]"
        dir="rtl"
      >
        <div className="max-w-xl mx-auto px-4 py-16 text-center">
          <div className="bg-white rounded-[24px] border border-[#E6E0D6] p-10 shadow-sm">
            <div className="w-16 h-16 rounded-full bg-[#F5F2EC] flex items-center justify-center mx-auto mb-5">
              <CheckCircle size={32} weight="duotone" className="text-[#4A641F]" />
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold text-[#1F3D2B] mb-3 font-['IBM_Plex_Sans_Arabic']">
              شكراً لكِ
            </h2>
            <p className="text-[#6B7565] leading-9 mb-7">
              تم استلام ملفك المهني وسيتم مراجعته وفقاً للمعايير المهنية لآفاق
              الطفولة.
            </p>
            <button
              onClick={() => navigate('/talent-bank')}
              data-testid="back-to-talent-bank"
              className="bg-[#1F3D2B] hover:bg-[#163020] text-white rounded-xl px-7 py-3 text-sm font-bold inline-flex items-center gap-2 transition-colors"
            >
              العودة لبنك الكفاءات
              <ArrowLeft size={16} className="rtl-flip" />
            </button>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main
      data-testid="create-profile-page"
      className="pt-24 pb-20 min-h-screen bg-[#FAFAF7]"
      dir="rtl"
    >
      <section className="py-10 bg-[#F5F2EC]">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <span className="inline-block px-4 py-1.5 rounded-full border border-[#D8D2C8] text-[#5D6E55] text-xs sm:text-sm mb-4">
            بنك الكفاءات التربوية
          </span>
          <h1 className="text-3xl sm:text-4xl font-bold text-[#1F3D2B] mb-4 font-['IBM_Plex_Sans_Arabic']">
            إنشاء ملف مهني
          </h1>
          <p className="text-[#6B7565] leading-8 text-sm sm:text-base">
            أنشئ ملفك المهني مجانًا، وانضم إلى مجتمع المستشارين المتعاونين مع
            Childhood Horizons. يتيح لك الملف توثيق مسارك المهني في الطفولة
            المبكرة، والاستفادة من محتوى التطوير المهني، والدخول ضمن فرص
            التعاون المستقبلية داخل Childhood Horizons — مع الحفاظ على سرية
            بياناتك بالكامل.
          </p>

          <div
            data-testid="profile-scope-notice"
            className="mt-5 mx-auto max-w-2xl text-right bg-white border border-[#D8D2C8] rounded-xl px-5 py-4"
          >
            <p className="text-[#1F3D2B] text-xs sm:text-sm font-bold mb-1">
              تنبيه
            </p>
            <p className="text-[#5D6E55] text-xs sm:text-sm leading-7">
              يهدف الملف المهني إلى توثيق ملفك ضمن مجتمع المستشارين المتعاونين
              في Childhood Horizons، ولا يمثل خدمة توظيف أو ضمان فرص عمل.
            </p>
          </div>

          <p className="text-xs text-[#92A082] mt-3">
            <Link to="/talent-bank" className="hover:text-[#4A641F]">
              ← العودة لبنك الكفاءات
            </Link>
          </p>
        </div>
      </section>

      <section className="py-10">
        <motion.form
          onSubmit={handleSubmit}
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-3xl mx-auto px-4"
        >
          <div className="bg-white rounded-[24px] border border-[#E6E0D6] p-5 sm:p-8 shadow-sm space-y-8">
            {/* Personal */}
            <section>
              <h3 className="text-lg sm:text-xl font-bold text-[#1F3D2B] mb-5 flex items-center gap-2 font-['IBM_Plex_Sans_Arabic']">
                <User size={20} weight="duotone" className="text-[#4A641F]" />
                المعلومات الشخصية
              </h3>
              <div className="grid sm:grid-cols-2 gap-4">
                {field({ label: 'الاسم الكامل', name: 'full_name', required: true, placeholder: 'الاسم الكامل' })}
                {field({ label: 'المدينة', name: 'city', required: true, placeholder: 'مثال: الرياض' })}
                {field({ label: 'الجنسية', name: 'nationality', placeholder: 'الجنسية' })}
                {field({ label: 'البريد الإلكتروني', name: 'email', type: 'email', required: true, placeholder: 'example@email.com', dir: 'ltr' })}
                {field({ label: 'رقم الجوال', name: 'mobile', required: true, placeholder: '05xxxxxxxx', dir: 'ltr' })}
              </div>
            </section>

            {/* Background */}
            <section>
              <h3 className="text-lg sm:text-xl font-bold text-[#1F3D2B] mb-5 flex items-center gap-2 font-['IBM_Plex_Sans_Arabic']">
                <GraduationCap size={20} weight="duotone" className="text-[#4A641F]" />
                الخلفية المهنية
              </h3>
              <div className="grid sm:grid-cols-2 gap-4">
                {field({ label: 'المؤهل العلمي', name: 'academic_qualification', required: true, placeholder: 'مثال: بكالوريوس تربية طفولة مبكرة' })}
                {field({ label: 'التخصص', name: 'specialization', required: true, placeholder: 'مثال: رياض أطفال، طفولة مبكرة، مناهج، طرق تدريس' })}
                {field({ label: 'سنوات الخبرة', name: 'years_of_experience', required: true, placeholder: 'مثال: 5 سنوات' })}
                {field({ label: 'المنصب الحالي أو السابق', name: 'current_or_previous_role', placeholder: 'مثال: معلمة رئيسية' })}
              </div>
            </section>

            {/* Skills */}
            <section>
              <h3 className="text-lg sm:text-xl font-bold text-[#1F3D2B] mb-5 flex items-center gap-2 font-['IBM_Plex_Sans_Arabic']">
                <Briefcase size={20} weight="duotone" className="text-[#4A641F]" />
                المهارات والشهادات
              </h3>
              <div className="space-y-4">
                {field({ label: 'المهارات المهنية', name: 'professional_skills', textarea: true, placeholder: 'اذكر مهاراتك المهنية الرئيسية…' })}
                {field({ label: 'الدورات والشهادات', name: 'courses_and_certifications', textarea: true, placeholder: 'اذكر الدورات والشهادات ذات الصلة…' })}
              </div>
            </section>

            {/* Preferences */}
            <section>
              <h3 className="text-lg sm:text-xl font-bold text-[#1F3D2B] mb-5 flex items-center gap-2 font-['IBM_Plex_Sans_Arabic']">
                <MapPin size={20} weight="duotone" className="text-[#4A641F]" />
                التفضيلات
              </h3>
              <div className="grid sm:grid-cols-2 gap-4">
                {field({ label: 'مجالات الاهتمام المهني', name: 'preferred_areas_of_interest', placeholder: 'مثال: تطوير المناهج' })}
                {field({ label: 'المدينة أو موقع العمل المفضل', name: 'preferred_city_or_work_location', placeholder: 'مثال: الرياض، عن بُعد' })}
              </div>

              <div className="mt-4">
                <label className="text-sm font-medium text-[#1F3D2B] block mb-2">
                  طبيعة العمل المرغوبة
                </label>
                <div
                  data-testid="input-preferred-work-mode"
                  className="grid sm:grid-cols-2 gap-2.5"
                >
                  {WORK_MODES.map((opt) => {
                    const active = formData.preferred_work_mode === opt.value;
                    return (
                      <label
                        key={opt.value}
                        className={`flex items-center gap-2.5 rounded-xl border px-4 py-3 cursor-pointer transition-colors ${
                          active
                            ? 'border-[#4A641F] bg-[#F5F2EC]'
                            : 'border-[#D8D2C8] bg-white hover:bg-[#FAFAF7]'
                        }`}
                      >
                        <input
                          type="radio"
                          name="preferred_work_mode"
                          value={opt.value}
                          checked={active}
                          onChange={handleChange}
                          className="w-4 h-4 text-[#4A641F] focus:ring-[#4A641F]"
                          data-testid={`work-mode-${opt.value}`}
                        />
                        <span className="text-sm text-[#3D512D]">
                          {opt.label}
                        </span>
                      </label>
                    );
                  })}
                </div>
                <p className="text-xs text-[#92A082] mt-2">
                  اختياري — يُساعدنا في مطابقة فرص التعاون المستقبلية مع تفضيلاتك.
                </p>
              </div>

              <div className="mt-4">
                <label className="text-sm font-medium text-[#1F3D2B] block mb-2">
                  هل أنت حاصل على رخصة مستشار؟
                </label>
                <div
                  data-testid="input-has-consultant-license"
                  className="grid grid-cols-2 gap-2.5"
                >
                  {[
                    { value: 'yes', label: 'نعم' },
                    { value: 'no', label: 'لا' },
                  ].map((opt) => {
                    const active = formData.has_consultant_license === opt.value;
                    return (
                      <label
                        key={opt.value}
                        className={`flex items-center gap-2.5 rounded-xl border px-4 py-3 cursor-pointer transition-colors ${
                          active
                            ? 'border-[#4A641F] bg-[#F5F2EC]'
                            : 'border-[#D8D2C8] bg-white hover:bg-[#FAFAF7]'
                        }`}
                      >
                        <input
                          type="radio"
                          name="has_consultant_license"
                          value={opt.value}
                          checked={active}
                          onChange={handleChange}
                          className="w-4 h-4 text-[#4A641F] focus:ring-[#4A641F]"
                          data-testid={`license-${opt.value}`}
                        />
                        <span className="text-sm text-[#3D512D]">
                          {opt.label}
                        </span>
                      </label>
                    );
                  })}
                </div>
                {formData.has_consultant_license === 'yes' && (
                  <div className="mt-3">
                    {field({
                      label: 'جهة الترخيص',
                      name: 'licensing_authority',
                      placeholder: 'مثال: وزارة التجارة، المركز السعودي للأعمال…',
                    })}
                  </div>
                )}
              </div>
            </section>

            {/* Additional */}
            <section>
              <h3 className="text-lg sm:text-xl font-bold text-[#1F3D2B] mb-5 flex items-center gap-2 font-['IBM_Plex_Sans_Arabic']">
                <FileText size={20} weight="duotone" className="text-[#4A641F]" />
                معلومات إضافية
              </h3>
              <div className="space-y-4">
                {field({ label: 'نبذة مهنية مختصرة', name: 'short_professional_bio', textarea: true, placeholder: 'ملخص مهني موجز…' })}

                {/* CV upload */}
                <div className="space-y-1.5">
                  <label className="text-sm font-medium text-[#1F3D2B] block">
                    رفع السيرة الذاتية (PDF، الحد الأقصى {MAX_CV_MB} ميجابايت)
                  </label>
                  <div className="border-2 border-dashed border-[#D8D2C8] rounded-xl p-6 text-center hover:border-[#4A641F] transition-colors bg-[#FAFAF7]">
                    <input
                      type="file"
                      accept=".pdf"
                      onChange={handleCvUpload}
                      id="cv-upload"
                      className="hidden"
                      data-testid="input-cv-upload"
                    />
                    <label htmlFor="cv-upload" className="cursor-pointer block">
                      {cvUploading ? (
                        <div className="flex items-center justify-center gap-2 text-[#1F3D2B]">
                          <div className="animate-spin w-5 h-5 border-2 border-[#1F3D2B] border-t-transparent rounded-full" />
                          جاري الرفع…
                        </div>
                      ) : cvFile ? (
                        <div className="flex items-center justify-center gap-2 text-[#4A641F]">
                          <CheckCircle size={20} weight="fill" />
                          <span className="text-sm">{cvFile.name}</span>
                        </div>
                      ) : (
                        <div>
                          <Upload size={28} weight="duotone" className="text-[#4A641F] mx-auto mb-2" />
                          <p className="text-sm text-[#3D512D]">انقر لرفع سيرتك الذاتية</p>
                          <p className="text-xs text-[#92A082] mt-1">
                            PDF فقط — حتى {MAX_CV_MB} ميجابايت
                          </p>
                        </div>
                      )}
                    </label>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <LinkedinLogo size={20} className="text-[#0077B5]" />
                  <div className="flex-1">
                    {field({
                      label: 'رابط حساب LinkedIn (اختياري)',
                      name: 'linkedin_profile_url',
                      placeholder: 'https://linkedin.com/in/yourprofile',
                      dir: 'ltr',
                    })}
                  </div>
                </div>
              </div>
            </section>

            {/* Consent */}
            <div>
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  name="consent_accepted"
                  checked={formData.consent_accepted}
                  onChange={handleChange}
                  className="mt-1 w-5 h-5 rounded border-[#D8D2C8] text-[#4A641F] focus:ring-[#4A641F]"
                  data-testid="input-consent"
                />
                <span className="text-sm text-[#3D512D] leading-7">
                  أوافق على{' '}
                  <a
                    href="/legal/privacy-policy"
                    target="_blank"
                    rel="noreferrer"
                    className="text-[#4A641F] hover:underline font-medium"
                  >
                    سياسة الخصوصية
                  </a>{' '}
                  و
                  <a
                    href="/legal/terms-conditions"
                    target="_blank"
                    rel="noreferrer"
                    className="text-[#4A641F] hover:underline font-medium"
                  >
                    شروط استخدام الملف المهني
                  </a>
                  ، وأقر بأن إنشاء الملف لا يمثل خدمة توظيف أو ضمان فرص عمل.
                </span>
              </label>
              {errors.consent_accepted && (
                <p className="text-red-500 text-xs mt-2">{errors.consent_accepted}</p>
              )}
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="submit-profile-btn"
              className="w-full bg-[#1F3D2B] hover:bg-[#163020] text-white rounded-xl px-7 py-3.5 text-sm font-bold flex items-center justify-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full" />
                  جاري الإرسال…
                </>
              ) : (
                <>
                  إرسال الملف المهني
                  <ArrowLeft size={16} className="rtl-flip" />
                </>
              )}
            </button>
          </div>
        </motion.form>
      </section>
    </main>
  );
};

export default CreateProfilePage;
